import React, { useEffect, useState } from 'react';
import { ShieldOff, ShieldAlert, Loader2 } from 'lucide-react';
import { listBlocked, unblockUser } from '../api/blocks';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

/**
 * BlockedUsersSection — Phase 27.8.
 *
 * Rendered inside SettingsPage. Lists all the users the current user
 * has blocked, with a per-row "Sblocca" button.
 */
const BlockedUsersSection = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await listBlocked();
      setItems(d);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleUnblock = async (username) => {
    setBusy(username);
    try {
      await unblockUser(username);
      toast({ title: `@${username} sbloccato`, description: 'Può di nuovo interagire con te.' });
      setItems((prev) => prev.filter((u) => u.username !== username));
    } catch (e) {
      toast({
        title: 'Impossibile sbloccare',
        description: e.response?.data?.detail || 'Riprova fra poco.',
        variant: 'destructive',
      });
    } finally { setBusy(null); }
  };

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="blocked-users-section"
    >
      <div className="mb-3 flex items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-red-500" />
        <h3 className="text-base font-semibold text-foreground">Utenti bloccati</h3>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Gli utenti che hai bloccato non possono seguirti, scriverti, commentare i tuoi post,
        chiederti di partecipare alle tue live.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Caricamento…
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border py-6 text-center text-sm text-muted-foreground" data-testid="blocked-users-empty">
          Non hai bloccato nessuno.
        </p>
      ) : (
        <ul className="divide-y divide-border/60" data-testid="blocked-users-list">
          {items.map((u) => (
            <li key={u.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`blocked-user-row-${u.username}`}>
              <div className="flex min-w-0 items-center gap-3">
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt={u.username} className="h-9 w-9 shrink-0 rounded-full object-cover object-center" />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold uppercase">
                    {u.username.charAt(0)}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">@{u.username}</p>
                  {u.full_name && (
                    <p className="truncate text-[11px] text-muted-foreground">{u.full_name}</p>
                  )}
                </div>
              </div>
              <Button
                onClick={() => handleUnblock(u.username)}
                disabled={busy === u.username}
                size="sm"
                variant="ghost"
                className="h-8 border border-border text-xs"
                data-testid={`unblock-btn-${u.username}`}
              >
                {busy === u.username
                  ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  : <ShieldOff className="mr-1 h-3.5 w-3.5" />}
                Sblocca
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default BlockedUsersSection;
